import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getCurrentUser } from '../../utils/auth';
import { fetchLookoutInbox } from '../../services/lookoutShareService';

const POLL_INTERVAL_MS = 45_000; // 45 seconds

/**
 * Polls the Lookout List share inbox and toasts when a NEW item shows up -
 * works no matter which page the user is on, same as ReminderWatcher.
 * The first check each session just captures a baseline (so existing,
 * already-waiting shares don't re-toast on every login/reload); only shares
 * that appear in a later poll get toasted.
 */
export function useLookoutShareNotifications() {
  const username = getCurrentUser();
  const navigate = useNavigate();
  const seenIdsRef = useRef(null); // null = baseline not yet captured this session
  const isFetchingRef = useRef(false);

  const checkForNewShares = useCallback(async () => {
    if (!username || username === 'System') return;
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    try {
      const inbox = await fetchLookoutInbox(username);
      const currentIds = new Set(inbox.map(share => share.ID));

      if (seenIdsRef.current === null) {
        seenIdsRef.current = currentIds;
        return;
      }

      inbox.forEach(share => {
        if (seenIdsRef.current.has(share.ID)) return;

        const message = share.CustomerName
          ? `${share.SharedBy} shared order ${share.OrderNo} (${share.CustomerName}) with you`
          : `${share.SharedBy} shared order ${share.OrderNo} with you`;

        toast.info(message, {
          toastId: `lookout-share-${share.ID}`,
          onClick: () => navigate('/past-due-tasks')
        });
      });

      seenIdsRef.current = currentIds;
    } catch (err) {
      console.error('[LookoutShareNotifications]', err);
    } finally {
      isFetchingRef.current = false;
    }
  }, [username, navigate]);

  useEffect(() => {
    if (!username || username === 'System') return;

    checkForNewShares();
    const interval = setInterval(checkForNewShares, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [checkForNewShares, username]);
}
